import React, { useRef, useState } from 'react';
import { Button, Typography, Box, Skeleton, IconButton, Slider } from '@mui/material';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import WaveSurferEditor from './WaveSurferEditor';
import { milliSecondsToFormattedTime } from '@utils/milliSecondsToFormattedTime';
import { useEditorHistory } from '@modules/editor';

interface EditorTimelineProps {
    videoDurationTime: number;
}

function EditorTimeline({ videoDurationTime }: EditorTimelineProps) {
    const wavesurfer = useRef(null);
    const [zoomLevel, setZoomLevel] = useState(50);
    const playTime = useSelector((state: any) => state.media.playTime);
    const { editHistory, editHistoryFlag, undo, redo } = useEditorHistory();
    const { t } = useTranslation();

    const handleZoomChange = (event: Event, newValue: number | number[]) => {
        setZoomLevel(newValue as number);
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(100, prev + 10));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(0, prev - 10));
    };

    if (videoDurationTime <= 0) {
        return (
            <Box className="editor-tab__skeleton-container">
                <Box className="editor-tab__skeleton-header">
                    <Skeleton variant="rectangular" width={150} height={10} />
                    <Skeleton variant="rectangular" width={100} height={10} />
                </Box>
                <Box className="editor-tab__skeleton-content">
                    {[...Array(80)].map((_, i) => (
                        <Skeleton key={i} variant="rectangular" width="100%" height={20 + Math.random() * 20} className="editor-tab__skeleton-item" />
                    ))}
                </Box>
                <Skeleton variant="rectangular" height={20} width="100%" />
            </Box>
        );
    }

    return (
        <div className="footer-editor">
            <div className="timeline-container">
                <div className="timeline-layout">
                    <div className="col-play-time">
                        <div className="timeline-history">
                            <Button
                                variant="contained"
                                className={`timeline-history-button ${editHistoryFlag <= -1 ? 'timeline-history-button--disabled' : ''}`}
                                onClick={undo}
                                disabled={editHistoryFlag <= -1}
                                startIcon={<UndoIcon />}
                            >
                                {t('vod.manage.editor_tab.undo')}
                            </Button>
                            <Button
                                variant="contained"
                                className={`timeline-history-button ${editHistoryFlag > editHistory.length - 1 ? 'timeline-history-button--disabled' : ''}`}
                                onClick={redo}
                                disabled={editHistoryFlag > editHistory.length - 1}
                                startIcon={<RedoIcon />}
                            >
                                {t('vod.manage.editor_tab.redo')}
                            </Button>
                        </div>
                        <Typography variant="body2">{milliSecondsToFormattedTime(playTime)}</Typography>
                    </div>
                    <div className="col-zoom-slider">
                        <Box className='box-zoom-slider'>
                            <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 0}>
                                <ZoomOutIcon fontSize="small" />
                            </IconButton>
                            <Slider
                                value={zoomLevel}
                                onChange={handleZoomChange}
                                min={0}
                                max={100}
                                step={1}
                                className="editor-tab__zoom-slider"
                            />
                            <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 100}>
                                <ZoomInIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </div>
                </div>
            </div>
            <div className="editor-timeline-wrapper">
                <WaveSurferEditor wavesurfer={wavesurfer} zoomLevel={zoomLevel} videoDurationTime={videoDurationTime} />
            </div>
        </div>
    );
}

export default EditorTimeline; 